/**
 * Post-processing pipeline that applies manipulation effects
 * to generated AIS positions.
 *
 * Each effect operates on positions whose timestamp falls within the
 * manipulation's time range (startFraction..endFraction of total duration).
 */

import type { AisPosition } from '../types/generated-data';
import type {
  TrackManipulation,
  GpsSpoofing,
  GpsJamming,
} from '../types/manipulation';

// ---------------------------------------------------------------------------
// Seeded PRNG (Mulberry32) — deterministic random from manipulation id
// ---------------------------------------------------------------------------

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Get the time fraction of a position within the track's overall duration. */
function positionFraction(
  pos: AisPosition,
  startMs: number,
  durationMs: number,
): number {
  if (durationMs === 0) return 0;
  const posMs = new Date(pos.timestamp).getTime();
  return (posMs - startMs) / durationMs;
}

/** Check if a position falls within a manipulation's time range. */
function isInRange(fraction: number, m: TrackManipulation): boolean {
  return fraction >= m.startFraction && fraction <= m.endFraction;
}

// ---------------------------------------------------------------------------
// Effect: GPS Spoofing — tag positions as spoofed (coordinate offsets are
// applied at the waypoint/path level in the editor, so the generated
// positions already contain the spoofed coordinates)
// ---------------------------------------------------------------------------

function applySpoofing(
  positions: AisPosition[],
  spoof: GpsSpoofing,
  startMs: number,
  durationMs: number,
): AisPosition[] {
  return positions.map((pos) => {
    const f = positionFraction(pos, startMs, durationMs);
    if (!isInRange(f, spoof)) return pos;
    return { ...pos, anomaly: 'gps_spoofing' };
  });
}

// ---------------------------------------------------------------------------
// Effect: GPS Jamming — drop messages + add coordinate noise
// ---------------------------------------------------------------------------

function applyJamming(
  positions: AisPosition[],
  jam: GpsJamming,
  startMs: number,
  durationMs: number,
): AisPosition[] {
  const rand = mulberry32(hashString(jam.id));
  const { noiseDeg, dropRate } = jam.config;

  // Count positions in the jammed range to gauge density
  let inRangeCount = 0;
  for (const pos of positions) {
    const f = positionFraction(pos, startMs, durationMs);
    if (isInRange(f, jam)) inRangeCount++;
  }

  // If density is very low (≤5 positions in range), reduce drop rate to avoid
  // removing almost everything
  const effectiveDropRate = inRangeCount <= 5 ? dropRate * 0.25 : dropRate;

  return positions.reduce<AisPosition[]>((acc, pos) => {
    const f = positionFraction(pos, startMs, durationMs);
    if (!isInRange(f, jam)) {
      acc.push(pos);
      return acc;
    }

    // Drop some positions
    if (rand() < effectiveDropRate) return acc;

    // Add noise to remaining
    acc.push({
      ...pos,
      latitude: Number(
        (pos.latitude + (rand() - 0.5) * 2 * noiseDeg).toFixed(6),
      ),
      longitude: Number(
        (pos.longitude + (rand() - 0.5) * 2 * noiseDeg).toFixed(6),
      ),
      anomaly: 'gps_jamming',
    });
    return acc;
  }, []);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Apply all enabled manipulations to a list of generated AIS positions.
 *
 * @param positions  - Generated positions (will NOT be mutated)
 * @param manipulations - Track manipulations to apply
 * @param startMs    - Scenario start time in ms since epoch
 * @param durationMs - Scenario total duration in ms
 * @returns New positions array with all effects applied
 */
export function applyManipulations(
  positions: AisPosition[],
  manipulations: TrackManipulation[] | undefined,
  startMs: number,
  durationMs: number,
): AisPosition[] {
  if (!manipulations || manipulations.length === 0) return positions;

  let result = positions;

  for (const m of manipulations) {
    if (!m.enabled) continue;

    switch (m.type) {
      case 'gps_spoofing':
        result = applySpoofing(result, m, startMs, durationMs);
        break;
      case 'gps_jamming':
        result = applyJamming(result, m, startMs, durationMs);
        break;
    }
  }

  return result;
}
